import React from 'react';
import { Shield, Eye, Lock, Phone, Mail, FileText, AlertTriangle } from 'lucide-react';

import { Banner } from './Banner';

export const HipaaNotice = () => {
  return (
    <div className="page-content">
      {/* Shared Banner + Pill (reduced) with top spacing */}
      <Banner pillText="HIPAA Notice of Privacy Practices" reduced={true} withTopSpace={true} />
      {/* Hero Section */}
      <section className="pad-120 bg-gradient-to-br from-black to-gray-900">
        <div className="container">
          <div className="text-center max-w-4xl mx-auto">
            <Shield style={{ color: 'var(--brand-primary)' }} size={64} className="mx-auto mb-6" />
            <h1 className="heading-1 mb-6">
              HIPAA Notice of Privacy Practices
            </h1>
            <p className="body-large mb-8">
              Your health information privacy rights and how your protected health information 
              may be used and disclosed at <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy.
            </p>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300">
                <strong>Effective Date:</strong> January 1, 2024 | <strong>Last Updated:</strong> January 1, 2024
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pad-120">
        <div className="container max-w-4xl mx-auto">
          
          {/* Introduction */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Your Rights Under HIPAA
            </h2>
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800 mb-8">
              <p className="body-medium text-gray-300 mb-4">
                This notice describes how medical information about you may be used and disclosed and 
                how you can get access to this information. <strong>Please review it carefully.</strong>
              </p>
              <p className="body-medium text-gray-300">
                We are required by law to maintain the privacy of protected health information, 
                provide individuals with notice of our legal duties and privacy practices, and 
                notify affected individuals following a breach of unsecured protected health information.
              </p>
            </div>
          </div>

          {/* Our Commitment */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Our Commitment to Your Privacy
            </h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="privacy-card bg-gray-900 rounded-xl p-6 border border-gray-800">
                <Lock style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Secure Storage</h3>
                <p className="body-small text-gray-400">Your health information is stored securely and accessed only by authorized personnel.</p>
              </div>
              <div className="privacy-card bg-gray-900 rounded-xl p-6 border border-gray-800">
                <Eye style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Limited Access</h3>
                <p className="body-small text-gray-400">Only healthcare providers directly involved in your care can access your information.</p>
              </div>
              <div className="privacy-card bg-gray-900 rounded-xl p-6 border border-gray-800">
                <FileText style={{ color: 'var(--brand-primary)' }} size={32} className="mb-4" />
                <h3 className="heading-4 mb-3" style={{ color: 'var(--brand-primary)' }}>Your Control</h3>
                <p className="body-small text-gray-400">You have rights to access, amend, and control how your health information is used.</p>
              </div>
            </div>
          </div>

          {/* How We Use Your Information */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              How We May Use and Disclose Your Health Information
            </h2>
            
            <div className="space-y-8">
              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>For Treatment</h3>
                <p className="body-medium text-gray-300 mb-4">
                  We use your health information to provide, coordinate, or manage your healthcare and related services.
                </p>
                <p className="body-small text-gray-400">
                  <strong>Example:</strong> Douglas Mitchell, DPT may share your treatment information with other healthcare 
                  providers at <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a> to coordinate your care.
                </p>
              </div>

              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>For Payment</h3>
                <p className="body-medium text-gray-300 mb-4">
                  We use your health information to obtain payment for services provided to you.
                </p>
                <p className="body-small text-gray-400">
                  <strong>Example:</strong> We may provide your health information to process payments from your 
                  Health Savings Account (HSA) or Flexible Spending Account (FSA).
                </p>
              </div>

              <div className="use-case bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>For Healthcare Operations</h3>
                <p className="body-medium text-gray-300 mb-4">
                  We use your health information for healthcare operations such as quality assessment, 
                  performance improvement, and staff training.
                </p>
                <p className="body-small text-gray-400">
                  <strong>Example:</strong> We may use your health information to evaluate the quality 
                  of care you received and improve our services.
                </p>
              </div>
            </div>
          </div>

          {/* Your Individual Rights */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Your Individual Rights
            </h2>
            
            <div className="space-y-6">
              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Access</h3>
                <p className="body-medium text-gray-300">
                  You have the right to inspect and copy your health information. You may request 
                  access by contacting our Privacy Officer.
                </p>
              </div>

              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Amend</h3>
                <p className="body-medium text-gray-300">
                  You have the right to request that we amend your health information if you believe 
                  it is incorrect or incomplete.
                </p>
              </div>

              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Restrict</h3>
                <p className="body-medium text-gray-300">
                  You have the right to request restrictions on how we use or disclose your health 
                  information for treatment, payment, or healthcare operations.
                </p>
              </div>

              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Confidential Communications</h3>
                <p className="body-medium text-gray-300">
                  You have the right to request that we communicate with you about your health information 
                  in a certain way or at a certain location.
                </p>
              </div>

              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Accounting</h3>
                <p className="body-medium text-gray-300">
                  You have the right to receive an accounting of disclosures of your health information 
                  made by us for certain purposes.
                </p>
              </div>

              <div className="right-item border-l-4 pl-6 py-4" style={{ borderColor: 'var(--brand-primary)' }}>
                <h3 className="heading-4 mb-2" style={{ color: 'var(--brand-primary)' }}>Right to Notification</h3>
                <p className="body-medium text-gray-300">
                  You have the right to be notified in the event of a breach of your unsecured 
                  protected health information.
                </p>
              </div>
            </div>
          </div>

          {/* Special Situations */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Special Situations
            </h2>
            
            <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
              <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>
                When We May Disclose Information Without Your Authorization
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>As required by law (court orders, subpoenas)</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>For public health activities</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>To report suspected abuse, neglect, or violence</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>For health oversight activities</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>In response to lawful requests by public officials</span>
                </li>
                <li className="flex items-start">
                  <div className="w-2 h-2 rounded-full mt-2 mr-3 flex-shrink-0" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                  <span>To avert serious threat to health or safety</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Contact Information */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Contact Information & Complaints
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8">
              <div className="contact-card bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Privacy Officer</h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <Phone style={{ color: 'var(--brand-primary)' }} size={20} className="mr-3" />
                    <span className="text-gray-300">(480) 555-0123</span>
                  </div>
                  <div className="flex items-center">
                    <Mail style={{ color: 'var(--brand-primary)' }} size={20} className="mr-3" />
                    <span className="text-gray-300">privacy@gymratrehab.com</span>
                  </div>
                </div>
              </div>

              <div className="contact-card bg-gray-900 rounded-xl p-6 border border-gray-800">
                <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Mailing Address</h3>
                <p className="text-gray-300">
                  <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> Physical Therapy<br />
                  c/o <a href="/favorite-places#alexander-medical-center" className="underline hover:no-underline" style={{ color: 'var(--brand-primary)' }}>Alexander Medical Center</a><br />
                  Gilbert, AZ 85234
                </p>
              </div>
            </div>

            <div className="mt-8 bg-amber-900/20 border border-amber-700/50 rounded-xl p-6">
              <div className="flex items-start">
                <AlertTriangle style={{ color: '#f59e0b' }} size={24} className="mr-4 mt-1 flex-shrink-0" />
                <div>
                  <h3 className="heading-4 mb-2" style={{ color: '#f59e0b' }}>Filing a Complaint</h3>
                  <p className="body-medium text-gray-300 mb-4">
                    You have the right to file a complaint if you believe your privacy rights have been violated. 
                    You can file a complaint with our Privacy Officer or with the U.S. Department of Health and Human Services.
                  </p>
                  <p className="body-small text-gray-400">
                    <strong>We will not retaliate against you for filing a complaint.</strong>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Changes to This Notice */}
          <div className="mb-12">
            <h2 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Changes to This Notice
            </h2>
            <div className="bg-gray-900 rounded-xl p-6 border border-gray-800">
              <p className="body-medium text-gray-300 mb-4">
                We reserve the right to change this notice and make the new notice effective for 
                health information we already have about you as well as any information we receive in the future.
              </p>
              <p className="body-medium text-gray-300">
                Current notices will be posted in our office and on our website. You may obtain a copy 
                of the current notice at any time by contacting our Privacy Officer.
              </p>
            </div>
          </div>

          {/* Legal Disclaimer */}
          <div className="bg-gray-900 rounded-xl p-8 border border-gray-800">
            <h3 className="heading-4 mb-4" style={{ color: 'var(--brand-primary)' }}>Legal Notice</h3>
            <p className="body-small text-gray-400 italic">
              This notice has been prepared to comply with HIPAA Privacy Rule requirements. 
              It should be reviewed by qualified legal counsel to ensure compliance with current 
              federal and state regulations. <span style={{ color: 'var(--secondary-brown)' }}>GymRatRehab</span> reserves the right to update this notice 
              as required by law or changes in our privacy practices.
            </p>
          </div>

        </div>
      </section>
    </div>
  );
};